import { GenNode } from '../types/carbon/models/GenNode'
import {
  getNodeID,
  canNodeHaveChildren,
  getNodeTitle,
} from '../utils/nodeTypes'
import { sortCodeframeChildren } from './axisHelpers'

export interface CheckboxState {
  enabled: boolean;
  checked: boolean;
  /** Set on a disabled box: drawn disabled with this as its hover text, never hidden. */
  reason?: string;
}

export const flattenTree = (nodes: GenNode[]): GenNode[] => {
  return nodes.reduce((acc: GenNode[], node) => {
    acc.push(node)
    if(node.children) {
      acc.push(...flattenTree(node.children))
    }
    return acc
  }, [])
}

/**
 * Hierarchical item ids (the Tree component's `parent/child` scheme, see
 * components/tree/utils.tsx convertNodesToTreeItems) of every node the TOC
 * says should open on load: the top-level Section/User/Folder nodes, plus any
 * node carrying the TOC's own expanded flag (`+` in the .toc file, parsed to
 * `isExpanded`). Tables, the dashboard table pickers and the chart editor all
 * open the same folders this way (#1229 B — the dashboard pickers used to
 * start fully collapsed).
 */
export const getInitialExpandedIds = (nodes: GenNode[]): string[] => {
  const expandedIds: string[] = []
  const walk = (nodeList: GenNode[], parentPath: string) => {
    for (const node of nodeList) {
      const baseId = node.id ? node.id.toString() : getNodeID(node)
      const hierarchicalId = parentPath ? `${parentPath}/${baseId}` : baseId
      const isTopLevel = !parentPath && (node.type === 'Section' || node.type === 'User' || node.type === 'Folder')
      if (isTopLevel || node.isExpanded) expandedIds.push(hierarchicalId)
      if (node.children) walk(node.children, hierarchicalId)
    }
  }
  walk(nodes, '')
  return expandedIds
}

export const processNodes = (nodes: GenNode[], processor: (node: GenNode) => GenNode | undefined): GenNode[] => {
  const results = nodes
    .map(node => {
      const processedNode = processor(node)
      if(!processedNode) return undefined
      const ret: GenNode = {
        ...processedNode,
        children: processedNode.children ? processNodes(processedNode.children, processor) : undefined,
      }
      return ret
    })
    .filter(node => node ? true : false)
  return results as GenNode[]
}

// IMPORTANT: this returns the .id of each gen-node - i.e. it's NOT the folder path
export const getNodeMap = (nodes: GenNode[]): Record<string, GenNode> => {
  const results: Record<string, GenNode> = {}
  processNodes(nodes, (node) => {
    results[getNodeID(node)] = node
    return node
  })
  return results
}

export const getAllDescendantIds = (node: GenNode, includeSelf = false): string[] => {
  const descendantIds: string[] = includeSelf ? [getNodeID(node)] : []
  if (node.children) {
    for (const child of node.children) {
      descendantIds.push(...getAllDescendantIds(child, true))
    }
  }
  return descendantIds
}

// Normalize text for search comparison: collapse dashes, underscores, and spaces
// so that "hello-world", "hello world", and "hello_world" all match each other
const normalizeForSearch = (text: string): string =>
  text.trim().toLowerCase().replace(/[-_\s]+/g, ' ')

// A table's subtitle is its side title (meta cbtSide, from the .cbt Side=
// line), the words researchers see under its name (#1917). The key is
// multitables' CBT_SIDE_META_KEY, named here because multitables imports this file.
const CBT_SIDE_META_KEY = 'cbtSide'
const tableSubtitle = (node: GenNode): string =>
  node.type === 'Table' ? node.meta?.find((m) => m.key === CBT_SIDE_META_KEY)?.value ?? '' : ''

// 'name' when the title holds the term, 'subtitle' when only a table's
// subtitle does, else null. Name matches list first.
const matchOf = (node: GenNode, term: string): 'name' | 'subtitle' | null => {
  if (normalizeForSearch(getNodeTitle(node)).includes(term)) return 'name'
  const sub = tableSubtitle(node)
  return sub && normalizeForSearch(sub).includes(term) ? 'subtitle' : null
}

export const searchNodes = (nodes: GenNode[], searchTerm: string): GenNode[] => {
  if (!searchTerm.trim()) {
    return nodes
  }

  const normalizedTerm = normalizeForSearch(searchTerm)
  const byName: GenNode[] = []
  const bySubtitle: GenNode[] = []

  processNodes(nodes, (node) => {
    const match = matchOf(node, normalizedTerm)
    if (match === 'name') byName.push({ ...node })
    else if (match === 'subtitle') bySubtitle.push({ ...node })
    return node
  })

  return [...byName, ...bySubtitle]
}

/**
 * Search that keeps the folder path: a folder (Folder/Section) is searched
 * through, and every other node (a Variable, a Table) is a match when its
 * title contains the term, or, for a Table, its subtitle (listed after the
 * name matches of the same folder, #1917). A matching node comes back whole (its children
 * untouched, so a Variable can still be opened for its codes), inside copies
 * of the folders that lead to it; folders with no match are dropped. Nodes
 * inside a non-folder (a Variable's codes) are not searched.
 */
export const searchNodesKeepingFolders = (nodes: GenNode[], searchTerm: string): GenNode[] => {
  const term = normalizeForSearch(searchTerm)
  if (!term) return nodes
  const walk = (list: GenNode[]): GenNode[] => {
    const out: GenNode[] = []
    const bySubtitle: GenNode[] = []
    for (const node of list) {
      if (node.type === 'Folder' || node.type === 'Section') {
        const kept = walk(node.children || [])
        if (kept.length > 0) out.push({ ...node, children: kept })
      } else {
        const match = matchOf(node, term)
        if (match === 'name') out.push(node)
        else if (match === 'subtitle') bySubtitle.push(node)
      }
    }
    return [...out, ...bySubtitle]
  }
  return walk(nodes)
}

export const findNodes = (nodes: GenNode[], processor: (node: GenNode) => boolean): GenNode[] => {
  const results: GenNode[] = []
  processNodes(nodes, (node) => {
    if (processor(node)) {
      results.push(node)
    }
    return node
  })
  return results
}

export const findNode = (nodes: GenNode[], processor: (node: GenNode) => boolean): GenNode | undefined => {
  const results = findNodes(nodes, processor)
  return results.length > 0 ? results[0] : undefined
}

export const calculateVartreeParentPaths = (nodes: GenNode[], parentPath: string = ''): GenNode[] => {
  return nodes.map(node => {
    const updatedNode: GenNode = {
      ...node,
      meta: (node.meta || []).concat([{ key: 'question', value: node.value2 }]),
      value2: parentPath
    }

    const currentPath = parentPath ? `${parentPath}/${node.value1}` : node.value1 || ''

    if (node.children && node.children.length > 0) {
      updatedNode.children = calculateVartreeParentPaths(node.children, currentPath)
    }

    return updatedNode
  })
}

// Get all ancestor folder IDs for a variable node, including the variable itself
// Returns composite IDs that match tree item ID format (e.g., "parent/child")
export const getAncestorFolderIds = (nodes: GenNode[], variableName: string): string[] => {
  const ancestorIds: string[] = []

  const findVariable = (nodeList: GenNode[], parentId = '', pathIds: string[] = []): boolean => {
    for (const node of nodeList) {
      const baseId = node.id?.toString() || getNodeID(node)
      const compositeId = parentId ? `${parentId}/${baseId}` : baseId
      const newPathIds = [...pathIds, compositeId]

      if (node.type === 'Variable' && node.value1 === variableName) {
        // Found it - add all ancestor folder IDs (excluding the variable itself)
        ancestorIds.push(...pathIds)
        return true
      }

      if (node.children && node.children.length > 0) {
        if (findVariable(node.children, compositeId, newPathIds)) {
          return true
        }
      }
    }
    return false
  }

  findVariable(nodes)
  return ancestorIds
}

// Get ancestor folder IDs from a folder path string like "General Opinions" or "Demographics/Age"
// Returns composite IDs that match tree item ID format (e.g., "parent/child")
export const getAncestorFolderIdsFromPath = (nodes: GenNode[], folderPath: string): string[] => {
  if (!folderPath) return []

  const ancestorIds: string[] = []
  const pathSegments = folderPath.split('/').filter(Boolean)

  const findFoldersInPath = (nodeList: GenNode[], remainingSegments: string[], parentId = ''): void => {
    if (remainingSegments.length === 0) return

    const targetSegment = remainingSegments[0]

    for (const node of nodeList) {
      if (node.value1 === targetSegment) {
        const baseId = node.id?.toString() || ''
        const compositeId = parentId ? `${parentId}/${baseId}` : baseId

        if (compositeId) {
          ancestorIds.push(compositeId)
        }

        if (remainingSegments.length > 1 && node.children) {
          findFoldersInPath(node.children, remainingSegments.slice(1), compositeId)
        }
        return
      }
    }
  }

  findFoldersInPath(nodes, pathSegments)
  return ancestorIds
}

/**
 * Recursively sorts all Codeframe children in a tree so that Base nodes appear before Code nodes
 */
export const sortNodesRecursively = (nodes: GenNode[]): GenNode[] => {
  return nodes.map(node => {
    let processedNode = { ...node }

    // If this is a Codeframe, sort its children
    if (node.type === 'Codeframe') {
      processedNode = sortCodeframeChildren(processedNode)
    }

    // Recursively process children
    if (processedNode.children && processedNode.children.length > 0) {
      processedNode.children = sortNodesRecursively(processedNode.children)
    }

    return processedNode
  })
}

